import type { components } from './schema';

/** Code used when a failed response carries no usable problem document. */
export const UNKNOWN_PROBLEM = 'unknown';

/** Code used when the request never reached the API. */
export const NETWORK_PROBLEM = 'network';

/** RFC 9457's default `type`: the status alone carries the semantics. */
const BLANK_TYPE = 'about:blank';

/**
 * A failed API call, normalised to RFC 9457 problem details so callers branch
 * on `code` and never on message text (ADR 0003).
 */
export class Problem extends Error {
  readonly code: string;
  /**
   * Problem type URI. Carried so a `Problem` is a complete `APIError`, which
   * is the error type the generated client declares.
   */
  readonly type: string;
  /** HTTP status, or 0 when the request never got a response. */
  readonly status: number;
  /** Field-level validation errors, when the API supplied them. */
  readonly errors: components['schemas']['ErrorDetail'][] | undefined;

  constructor(
    code: string,
    status: number,
    message: string,
    type = BLANK_TYPE,
    errors?: components['schemas']['ErrorDetail'][],
  ) {
    super(message);
    this.name = 'Problem';
    this.code = code;
    this.type = type;
    this.status = status;
    this.errors = errors;
  }
}

function readString(body: unknown, key: string): string | undefined {
  if (typeof body !== 'object' || body === null) {
    return undefined;
  }
  const value = (body as Record<string, unknown>)[key];
  return typeof value === 'string' && value !== '' ? value : undefined;
}

function isErrorDetail(
  value: unknown,
): value is components['schemas']['ErrorDetail'] {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  const detail = value as Record<string, unknown>;
  return (
    (detail.location === undefined || typeof detail.location === 'string') &&
    (detail.message === undefined || typeof detail.message === 'string')
  );
}

function readErrors(
  body: unknown,
): components['schemas']['ErrorDetail'][] | undefined {
  if (typeof body !== 'object' || body === null) {
    return undefined;
  }
  const value = (body as Record<string, unknown>).errors;
  return Array.isArray(value) && value.every(isErrorDetail) ? value : undefined;
}

/**
 * Turn a failed response — or whatever `fetch` rejected with — into one
 * `Problem`. Reading the response body is the only I/O it does.
 */
export async function toProblem(failure: unknown): Promise<Problem> {
  if (!(failure instanceof Response)) {
    const message =
      failure instanceof Error ? failure.message : 'The request failed.';
    return new Problem(NETWORK_PROBLEM, 0, message);
  }

  const body: unknown = await failure.json().catch(() => undefined);
  const code = readString(body, 'code') ?? UNKNOWN_PROBLEM;
  const message =
    readString(body, 'detail') ??
    readString(body, 'title') ??
    // Empty on HTTP/2, which has no reason phrase.
    (failure.statusText || 'The request failed.');

  return new Problem(
    code,
    failure.status,
    message,
    readString(body, 'type'),
    readErrors(body),
  );
}
